// src/components/usuario/PreguntasRespuestas.js
import React, { useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import backgroundImage from "../../assets/img/fondoNueve.png";
import logoCIE from "../../assets/img/logoCIE.svg";
import "../../assets/css/overlays.css";
import Lottie from "react-lottie";
import relojArenaJSON from "../../assets/relojArena.json";
import HeaderUsuario from "../header/HeaderUsuario";
import usePreguntasRespuestas from "../../hooks/usePreguntasRespuestas";
import useQuestionTimer from "../../hooks/useQuestionTimer";

// 🔊 Audios (ajusta nombres/extensiones si es necesario)
import sCorrecto from "../../assets/sounds/correcto.mp3";
import sIncorrecto from "../../assets/sounds/incorrecto.mp3";
import sAviso from "../../assets/sounds/aviso.mp3";
import sAgotado from "../../assets/sounds/agotado.mp3";

const COLORS = ["#7BAF4D", "#E2843E", "#D0D01E", "#5FB3D4"];

const PreguntasRespuestas = () => {
  const { tema } = useParams();
  const navigate = useNavigate();

  const {
    preguntaActual,
    tiempoRestante,
    nivelCompletado,
    tiempoAgotadoFlag,
    mostrarAlerta,
    alertaInfo,
    retroalimentacion,
    mensaje,
    mostrarResultado,
    handleRespuesta,
    avanzarPregunta,
    setMostrarAlerta,
    setMostrarResultado,
    formatTiempo,
  } = usePreguntasRespuestas(tema);

  // === Setup de audios (una vez) ===
  const audios = useMemo(() => {
    const make = (src, vol = 0.8) => {
      const a = new Audio(src);
      a.preload = "auto";
      a.volume = vol;
      return a;
    };
    return {
      correcto: make(sCorrecto, 0.8),
      incorrecto: make(sIncorrecto, 0.8),
      aviso: make(sAviso, 0.7),
      agotado: make(sAgotado, 0.85),
    };
  }, []);

  // Limpieza de audios al desmontar
  useEffect(() => {
    return () => {
      Object.values(audios).forEach((a) => {
        try {
          a.pause();
          a.currentTime = 0;
        } catch {}
      });
    };
  }, [audios]);

  // === Integración de cronómetro por pregunta (robusta) ===
  const token = useMemo(() => localStorage.getItem("token"), []);
  const sessionId = useMemo(
    () => Number(localStorage.getItem("sessionId") || 0),
    []
  );

  const safeInt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  // Intentamos varias propiedades posibles del backend
  const nivelIdForTimer = useMemo(() => {
    const candidates = [
      preguntaActual?.nivel_id,
      preguntaActual?.id_nivel,
      preguntaActual?.nivelId,
      preguntaActual?.nivel, // por si acaso
    ];
    for (const c of candidates) {
      const n = safeInt(c);
      if (n) return n;
    }
    return null;
  }, [preguntaActual]);

  const temaIdForTimer = useMemo(() => {
    const candidates = [
      preguntaActual?.tema_id,
      preguntaActual?.id_tema,
      preguntaActual?.temaId,
      tema, // parámetro de ruta
    ];
    for (const c of candidates) {
      const n = safeInt(c);
      if (n) return n;
    }
    return null;
  }, [preguntaActual, tema]);

  const { startQuestion, endQuestion, pause, resume } = useQuestionTimer({
    token,
    sessionId,
    nivelId: nivelIdForTimer ?? 0,
    temaId: temaIdForTimer ?? 0,
  });

  // Arranca/termina el conteo cuando cambia la pregunta
  useEffect(() => {
    if (preguntaActual?.id) startQuestion(preguntaActual.id);
    return () => {
      // guarda si se cambia de pregunta o se desmonta
      endQuestion();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preguntaActual?.id]);

  // Pausar/reanudar cuando haya overlays
  const isOverlayOpen = !!(
    mostrarAlerta ||
    mostrarResultado ||
    tiempoAgotadoFlag
  );
  useEffect(() => {
    if (isOverlayOpen) pause();
    else resume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlayOpen]);

  // === Sonidos: correcta/incorrecta al abrir overlay (si no es por tiempo agotado) ===
  useEffect(() => {
    if (!mostrarAlerta || tiempoAgotadoFlag) return;
    const a = alertaInfo?.esCorrecta ? audios.correcto : audios.incorrecto;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch {}
  }, [mostrarAlerta, alertaInfo?.esCorrecta, tiempoAgotadoFlag, audios]);

  // === Sonido: aviso de poco tiempo (una sola vez por pregunta) ===
  const WARN_THRESHOLD = 10; // segundos para avisar
  const warnPlayedRef = useRef(false);

  // Reinicia el aviso cuando cambia la pregunta
  useEffect(() => {
    warnPlayedRef.current = false;
  }, [preguntaActual?.id]);

  useEffect(() => {
    if (
      typeof tiempoRestante === "number" &&
      tiempoRestante > 0 &&
      tiempoRestante <= WARN_THRESHOLD &&
      !warnPlayedRef.current &&
      !tiempoAgotadoFlag
    ) {
      try {
        audios.aviso.currentTime = 0;
        audios.aviso.play().catch(() => {});
      } catch {}
      warnPlayedRef.current = true;
    }
  }, [tiempoRestante, tiempoAgotadoFlag, audios]);

  // === Sonido: tiempo agotado ===
  useEffect(() => {
    if (!tiempoAgotadoFlag) return;
    try {
      audios.agotado.currentTime = 0;
      audios.agotado.play().catch(() => {});
    } catch {}
  }, [tiempoAgotadoFlag, audios]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: relojArenaJSON,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
  };

  if (!preguntaActual) return <p>Cargando preguntas...</p>;

  const esPreguntaVerdaderoFalso =
    preguntaActual.respuestas.length === 2 &&
    preguntaActual.respuestas.every((r) =>
      ["Verdadero", "Falso"].includes(r.texto_respuesta)
    );

  const respuestasValidas = preguntaActual.respuestas.filter(
    (r) => r.texto_respuesta.trim() !== ""
  );

  return (
    <div className="min-h-screen flex flex-col font-montserrat">
      <div className="w-full bg-white shadow-md z-10 p-4 flex-shrink-0">
        <p className="text-base font-montserrat font-bold sm:text-lg md:text-xl lg:text-3xl text-center text-green-700 break-words">
          {preguntaActual.texto_pregunta}
        </p>
      </div>

      <div
        className="flex-grow relative flex flex-col"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute top-4 left-4 z-20">
          <div className="bg-transparent rounded-lg p-3 border">
            <p className="font-extrabold text-lg sm:text-xl md:text-2xl lg:text-3xl text-white">
              {formatTiempo(tiempoRestante)}
            </p>
          </div>
        </div>

        <div className="absolute top-4 right-4 z-10">
          <HeaderUsuario />
        </div>

        <div className="flex-grow flex justify-center items-center p-6 sm:p-8 md:p-10">
          {!nivelCompletado && !tiempoAgotadoFlag && (
            <>
              {esPreguntaVerdaderoFalso ? (
                <div className="flex flex-col sm:flex-row justify-center w-full max-w-4xl space-y-6 sm:space-y-0 sm:space-x-10">
                  <button
                    onClick={() =>
                      handleRespuesta(
                        preguntaActual.respuestas.find(
                          (r) => r.texto_respuesta === "Verdadero"
                        ).id
                      )
                    }
                    className="w-full sm:w-1/2 p-6 sm:p-8 md:p-10 rounded-tl-[60px] rounded-br-[60px] text-white font-bold transition-colors text-base sm:text-lg md:text-xl lg:text-2xl hover:brightness-90 shadow-lg"
                    style={{ backgroundColor: "#7BAF4D" }}
                  >
                    Verdadero
                  </button>
                  <button
                    onClick={() =>
                      handleRespuesta(
                        preguntaActual.respuestas.find(
                          (r) => r.texto_respuesta === "Falso"
                        ).id
                      )
                    }
                    className="w-full sm:w-1/2 p-6 sm:p-8 md:p-10 rounded-tl-[60px] rounded-br-[60px] text-white font-bold transition-colors text-base sm:text-lg md:text-xl lg:text-2xl hover:brightness-90 shadow-lg"
                    style={{ backgroundColor: "#F58634" }}
                  >
                    Falso
                  </button>
                </div>
              ) : (
                <div
                  className={`grid ${
                    respuestasValidas.length > 2
                      ? "grid-cols-1 sm:grid-cols-2"
                      : "grid-cols-1"
                  } gap-6 sm:gap-8 w-full max-w-5xl`}
                >
                  {respuestasValidas.map((respuesta, index) => (
                    <button
                      key={respuesta.id}
                      onClick={() => handleRespuesta(respuesta.id)}
                      className="w-full p-6 sm:p-8 md:p-10 rounded-tl-[60px] rounded-br-[60px] text-white font-bold transition-colors text-base sm:text-lg md:text-xl lg:text-2xl break-words hover:brightness-90 shadow-lg"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    >
                      {respuesta.texto_respuesta}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Botón inferior centrado → Contenido multimedia */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
          <button
            onClick={async () => {
              await endQuestion();
              navigate(`/tema/multimedia/${tema}`);
            }}
            className="bg-green-700 text-white px-6 py-3 rounded-full shadow-md font-bold hover:bg-green-800 transition"
            aria-label="Regresar"
          >
            Ver contenido
          </button>
        </div>

        {/* Logo */}
        <div className="absolute bottom-4 right-4 sm:bottom-5 sm:right-8 z-10">
          <img src={logoCIE} alt="Logo CIE" className="h-auto w-auto" />
        </div>

        {/* Alerta */}
        {mostrarAlerta && (
          <div
            className={`fixed inset-0 flex items-center justify-center z-50 ${
              tiempoAgotadoFlag
                ? "bg-black bg-opacity-50 backdrop-blur-sm"
                : alertaInfo.esCorrecta
                ? "bg-green-500 bg-opacity-50 backdrop-blur-sm"
                : "bg-red-500 bg-opacity-50 backdrop-blur-sm"
            }`}
          >
            <div className="flex flex-col items-center justify-center h-screen w-full max-w-[90%] md:max-w-4xl mx-auto py-4 md:py-6 px-3 md:px-4 space-y-4 md:space-y-6">
              {tiempoAgotadoFlag ? (
                <>
                  <div className="text-center w-full">
                    <h1 className="text-white text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 md:mb-3">
                      ¡TIEMPO AGOTADO!
                    </h1>
                    <h2 className="text-white text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold">
                      Inténtalo de nuevo
                    </h2>
                  </div>
                  <div className="w-24 h-24 xs:w-32 xs:h-32 sm:w-40 sm:h-40 md:w-56 md:h-56 lg:w-64 lg:h-64">
                    <Lottie
                      options={defaultOptions}
                      height="100%"
                      width="100%"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      await endQuestion();
                      setMostrarAlerta(false);
                      navigate(`/tema/multimedia/${tema}`);
                    }}
                    className="bg-white text-black font-bold py-2 px-4 xs:py-3 xs:px-6 rounded-full text-lg xs:text-xl sm:text-2xl md:text-3xl hover:bg-gray-100 transition-colors mt-2 md:mt-4"
                  >
                    Volver a Contenido
                  </button>
                </>
              ) : (
                <>
                  <div className="flex flex-col items-center max-w={[`90vw`]} max-h-[`90vh`] space-y-4 xs:space-y-6 sm:space-y-8 p-2 xs:p-3 sm:p-4 md:p-6">
                    <h1 className="text-white text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center z-10 leading-tight">
                      {alertaInfo.esCorrecta
                        ? "¡BIEN HECHO!"
                        : "¡HAZLO MEJOR EN LA PRÓXIMA!"}
                    </h1>

                    <div
                      className={`w-20 h-20 xs:w-24 xs:h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 border-4 sm:border-6 md:border-8 ${
                        alertaInfo.esCorrecta
                          ? "border-[#FFD900]"
                          : "border-[#FF0000]"
                      } rounded-full flex items-center justify-center p-2 sm:p-3`}
                    >
                      <p className="text-white text-base xs:text-lg sm:text-2xl md:text-3xl lg:text-4xl font-extrabold font-montserrat text-center">
                        {alertaInfo.mensaje}
                      </p>
                    </div>

                    {!alertaInfo.esCorrecta && retroalimentacion && (
                      <div className="text-white text-center w-full px-2 xs:px-3 sm:px-4">
                        <p className="font-bold text-xl xs:text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3 leading-tight">
                          Retroalimentación:
                        </p>
                        <p className="text-base xs:text-lg sm:text-xl md:text-2xl lg:text-3xl break-words leading-snug">
                          {retroalimentacion}
                        </p>
                      </div>
                    )}

                    <button
                      onClick={async () => {
                        setMostrarAlerta(false);
                        if (alertaInfo.esCorrecta) {
                          // Guardar tiempo de esta pregunta y pasar a la siguiente
                          await endQuestion();
                          avanzarPregunta();
                        } else {
                          // Incorrecta → solo cierra overlay; el timer se reanuda por el efecto
                        }
                      }}
                      className="bg-white text-black font-bold py-2 px-4 xs:py-3 xs:px-6 sm:py-3 sm:px-8 rounded-full text-lg xs:text-xl sm:text-2xl md:text-3xl hover:bg-gray-100 transition-colors mt-2 md:mt-4"
                    >
                      {alertaInfo.esCorrecta
                        ? "Continuar"
                        : "Volver a intentar"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Resumen del nivel */}
        {mensaje && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <h2 className="text-2xl font-bold mb-4">Resumen del tema</h2>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex flex-col items-center">
                  <div className="bg-[#15A054] rounded-full w-20 h-20 flex items-center justify-center mb-2">
                    <span className="text-white font-bold text-2xl">
                      {mensaje.respuestasCorrectas}
                    </span>
                  </div>
                  <span className="text-[#15A054] font-bold">Correctas</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="bg-[#E94E2D] rounded-full w-20 h-20 flex items-center justify-center mb-2">
                    <span className="text-white font-bold text-2xl">
                      {mensaje.respuestasIncorrectas}
                    </span>
                  </div>
                  <span className="text-[#E94E2D] font-bold">Incorrectas</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setMostrarResultado(false);
                  navigate(-2);
                }}
                className="bg-green-700 hover:bg-green-800 text-white font-bold py-2 px-4 rounded"
              >
                Continuar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreguntasRespuestas;
